import React, { useCallback, useEffect, useState } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    FileText,
} from 'lucide-react';
import moment from 'moment';
import { staffClient } from 'clients/staff';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import Table, { Column } from 'ui/Table';
import Button from 'ui/Button';
import Loading from 'ui/Loading';
import { Input } from 'ui/shadcn';
import { cn } from 'lib/utils';

interface DownloadLog {
    id: number;
    staffId: number | null;
    userId: string | null;
    staffName: string | null;
    ipAddress: string | null;
    fileName: string | null;
    fileUrl: string | null;
    siteId: number | null;
    siteName: string | null;
    boardId: number | null;
    boardName: string | null;
    categoryName: string | null;
    userAgent: string | null;
    created_at: string;
}

const PAGE_SIZE = 20;

const DownloadLogPanel: React.FC = () => {
    const [logs, setLogs] = useState<DownloadLog[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [loading, setLoading] = useState(true);
    const [initialized, setInitialized] = useState(false);

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params: Record<string, string | number> = {
                offset: (page - 1) * PAGE_SIZE,
                limit: PAGE_SIZE,
            };
            if (search.trim()) params.search = search.trim();
            if (from) params.from = from;
            if (to) params.to = to;

            const response = await staffClient.get('/download-log', { params });
            setLogs(response.data?.data || []);
            setTotal(response.data?.meta?.total ?? 0);
        } catch (error) {
            console.error('[DownloadLogs] 로그 조회 실패:', error);
            setLogs([]);
            setTotal(0);
        } finally {
            setLoading(false);
            setInitialized(true);
        }
    }, [page, search, from, to]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // 필터가 바뀌면 1페이지부터 다시 — 조건 변경과 페이지 이동이 겹쳐 두 번 요청되지 않게
    // 각 핸들러에서 페이지를 함께 초기화한다.
    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleFromChange = (value: string) => {
        setFrom(value);
        setPage(1);
    };

    const handleToChange = (value: string) => {
        setTo(value);
        setPage(1);
    };

    const handleReset = () => {
        setSearch('');
        setFrom('');
        setTo('');
        setPage(1);
    };

    const hasFilter = Boolean(search || from || to);
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const columns: Column<DownloadLog>[] = [
        {
            key: 'created_at',
            header: '일시',
            width: '150px',
            render: (value) => (
                <span className="text-muted-foreground tabular-nums">
                    {value ? moment(value).format('YYYY.MM.DD HH:mm:ss') : '-'}
                </span>
            ),
        },
        {
            key: 'staffName',
            header: '사용자',
            width: '140px',
            render: (_value, row) => (
                <div className="flex flex-col leading-tight">
                    <span className="font-medium text-foreground">
                        {row.staffName || '알 수 없음'}
                    </span>
                    {row.userId && (
                        <span className="text-xs text-muted-foreground">{row.userId}</span>
                    )}
                </div>
            ),
        },
        {
            key: 'ipAddress',
            header: 'IP 주소',
            width: '130px',
            render: (value) => (
                <span className="text-neutral-700 tabular-nums">{value || '-'}</span>
            ),
        },
        {
            key: 'fileName',
            header: '파일명',
            render: (value, row) => (
                <div className="flex min-w-0 items-center gap-2" title={value || ''}>
                    <FileText className="h-4 w-4 shrink-0 text-neutral-400" strokeWidth={1.5} />
                    {row.fileUrl ? (
                        <a
                            href={row.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="truncate text-foreground hover:text-primary hover:underline"
                        >
                            {value || '(파일명 없음)'}
                        </a>
                    ) : (
                        <span className="truncate text-foreground">{value || '(파일명 없음)'}</span>
                    )}
                </div>
            ),
        },
        {
            key: 'siteName',
            header: '현장',
            width: '180px',
            render: (value) => (
                <span className="block truncate" title={value || ''}>
                    {value || <span className="text-neutral-400">-</span>}
                </span>
            ),
        },
        {
            key: 'boardName',
            header: '게시판 / 카테고리',
            width: '180px',
            render: (_value, row) =>
                row.boardName || row.categoryName ? (
                    <div className="flex min-w-0 flex-col leading-tight">
                        <span className="truncate text-foreground">{row.boardName || '-'}</span>
                        {row.categoryName && (
                            <span className="truncate text-xs text-muted-foreground">
                                {row.categoryName}
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="text-neutral-400">-</span>
                ),
        },
    ];

    if (!initialized && loading) {
        return <Loading text="다운로드 로그를 불러오는 중..." />;
    }

    return (
        <div>
            <FilterSection>
                <SearchInput
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="사용자명, 아이디, 파일명, 현장명 검색..."
                />
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">기간</span>
                    <Input
                        type="date"
                        value={from}
                        max={to || undefined}
                        onChange={(e) => handleFromChange(e.target.value)}
                        className="w-[150px]"
                        aria-label="시작일"
                    />
                    <span className="text-muted-foreground">~</span>
                    <Input
                        type="date"
                        value={to}
                        min={from || undefined}
                        onChange={(e) => handleToChange(e.target.value)}
                        className="w-[150px]"
                        aria-label="종료일"
                    />
                </div>
                {hasFilter && (
                    <Button variant="outline" size="small" onClick={handleReset}>
                        초기화
                    </Button>
                )}
            </FilterSection>

            <TableContainer title="다운로드 이력" resultCount={total}>
                <Table
                    columns={columns}
                    data={logs}
                    loading={loading && logs.length === 0}
                    emptyMessage={
                        hasFilter
                            ? '조건에 맞는 다운로드 이력이 없습니다.'
                            : '다운로드 이력이 없습니다.'
                    }
                />
                <ServerPagination
                    currentPage={page}
                    totalPages={totalPages}
                    pageSize={PAGE_SIZE}
                    totalCount={total}
                    onPageChange={setPage}
                />
            </TableContainer>
        </div>
    );
};

// 서버 페이지네이션 — ui/Table 내장 페이저와 같은 모양
const ServerPagination: React.FC<{
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, pageSize, totalCount, onPageChange }) => {
    if (totalPages <= 1) return null;

    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    const pages: number[] = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const startIdx = (currentPage - 1) * pageSize + 1;
    const endIdx = Math.min(currentPage * pageSize, totalCount);

    return (
        <div className="flex items-center justify-between gap-3 border-t border-border bg-neutral-50 px-4 py-3">
            <div className="text-xs text-muted-foreground tabular-nums">
                <span className="font-medium text-foreground">
                    {startIdx}–{endIdx}
                </span>{' '}
                / 전체 <span className="font-medium text-foreground">{totalCount}</span>건
            </div>
            <div className="flex items-center gap-1">
                <PageButton disabled={currentPage === 1} onClick={() => onPageChange(1)} aria-label="처음">
                    <ChevronsLeft className="h-4 w-4" />
                </PageButton>
                <PageButton disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} aria-label="이전">
                    <ChevronLeft className="h-4 w-4" />
                </PageButton>
                {pages.map((p) => (
                    <PageButton key={p} active={p === currentPage} onClick={() => onPageChange(p)}>
                        <span className="tabular-nums">{p}</span>
                    </PageButton>
                ))}
                <PageButton disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} aria-label="다음">
                    <ChevronRight className="h-4 w-4" />
                </PageButton>
                <PageButton disabled={currentPage === totalPages} onClick={() => onPageChange(totalPages)} aria-label="마지막">
                    <ChevronsRight className="h-4 w-4" />
                </PageButton>
            </div>
        </div>
    );
};

const PageButton: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
> = ({ active, className, children, ...props }) => (
    <button
        type="button"
        className={cn(
            'inline-flex h-7 min-w-[28px] items-center justify-center rounded border border-transparent px-1.5 text-xs transition-colors duration-fast',
            active
                ? 'bg-primary text-primary-foreground'
                : 'text-neutral-700 hover:bg-muted disabled:text-neutral-400 disabled:hover:bg-transparent',
            'disabled:cursor-not-allowed',
            className
        )}
        {...props}
    >
        {children}
    </button>
);

export default DownloadLogPanel;
